import type { AvatarConfig } from '../avatar/types'

/** Weekly rotating event queue. Ops starts one mode at a time. */

export const EVENT_QUEUE_MODES = ['random_deck', 'unlimited', 'one_copy'] as const
export type EventQueueMode = (typeof EVENT_QUEUE_MODES)[number]

export function isEventQueueMode(value: unknown): value is EventQueueMode {
  return (EVENT_QUEUE_MODES as readonly string[]).includes(String(value))
}

/** 60-card main, one copy of each name. Extra still caps at 15. */
export const EVENT_QUEUE_ONE_COPY_MIN_MAIN = 60
export const EVENT_QUEUE_ONE_COPY_MAX_COPIES = 1

export type EventQueueDeckRules = {
  kind: 'owned' | 'random'
  minMain: number
  maxCopies: number
  houseBanlist: boolean
}

export function eventQueueDeckRules(mode: EventQueueMode): EventQueueDeckRules {
  if (mode === 'random_deck') {
    return {
      kind: 'random',
      minMain: 40,
      maxCopies: 3,
      houseBanlist: true,
    }
  }
  if (mode === 'one_copy') {
    return {
      kind: 'owned',
      minMain: EVENT_QUEUE_ONE_COPY_MIN_MAIN,
      maxCopies: EVENT_QUEUE_ONE_COPY_MAX_COPIES,
      houseBanlist: false,
    }
  }
  return {
    kind: 'owned',
    minMain: 40,
    maxCopies: 3,
    houseBanlist: false,
  }
}

/** How long a live event stays open after ops starts it. */
export const EVENT_QUEUE_DURATION_MS = 7 * 24 * 60 * 60 * 1000

/** Home explainer dialog auto-opens only in this window after start. */
export const EVENT_QUEUE_NOTICE_MS = 3 * 24 * 60 * 60 * 1000

export function eventQueueNoticeOpen(
  live: Pick<EventQueueLivePublic, 'startsAt' | 'endsAt'> | null | undefined,
  now = Date.now(),
): boolean {
  if (!live) return false
  const start = Date.parse(live.startsAt)
  const end = Date.parse(live.endsAt)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false
  if (now >= end) return false
  return now < start + EVENT_QUEUE_NOTICE_MS
}

/**
 * One band only, among accounts with at least one win.
 * `through` is the inclusive share of that field. A later band
 * starts after the earlier one, so the top 1% is not also paid as top 10%.
 */
export const EVENT_QUEUE_PACK_BANDS = [
  { through: 0.01, packs: 20 },
  { through: 0.10, packs: 15 },
  { through: 0.25, packs: 8 },
  { through: 0.50, packs: 4 },
] as const

/** Anyone with a win who sits below the top 50%. */
export const EVENT_QUEUE_REST_PACKS = 2

export type EventQueuePayout = { coins: number; packs: number }

export type EventQueuePayoutRow = {
  position: number
  wins: number
  losses: number
}

/**
 * Packs keyed by 1-based position.
 * The field is accounts with at least one win. Each cutoff rounds up,
 * and a tie on wins and losses that touches the cutoff takes the higher band.
 * Username does not split a prize. A row with no win is absent from the map.
 */
export function eventQueuePacksByPosition(
  rows: readonly EventQueuePayoutRow[],
): Map<number, number> {
  const qualified = rows
    .filter((row) => row.wins >= 1 && Number.isInteger(row.position) && row.position >= 1)
    .slice()
    .sort((a, b) => a.position - b.position)
  const packs = new Map<number, number>()
  const count = qualified.length
  if (count === 0) return packs

  let cursor = 0
  for (const band of EVENT_QUEUE_PACK_BANDS) {
    if (cursor >= count) break
    let endRank = Math.max(cursor + 1, Math.ceil(count * band.through))
    if (endRank > count) endRank = count
    const border = qualified[endRank - 1]
    while (
      endRank < count &&
      qualified[endRank].wins === border.wins &&
      qualified[endRank].losses === border.losses
    ) {
      endRank += 1
    }
    for (let index = cursor; index < endRank; index += 1) {
      packs.set(qualified[index].position, band.packs)
    }
    cursor = endRank
  }
  for (let index = cursor; index < count; index += 1) {
    packs.set(qualified[index].position, EVENT_QUEUE_REST_PACKS)
  }
  return packs
}

/** Public snapshot on `/site/status` so Home can show the button. */
export interface EventQueueLivePublic {
  mode: EventQueueMode
  startsAt: string
  endsAt: string
}

export interface EventQueueMailMeta {
  eventId: string
  mode: EventQueueMode
  position: number
  wins: number
}

export interface EventQueueStandingRow {
  userId: string
  username: string
  avatar: AvatarConfig
  wins: number
  losses: number
  draws: number
  position: number
}

export interface EventQueueBoard {
  mode: EventQueueMode
  status: 'live' | 'done'
  startsAt: string
  endsAt: string
  you: { wins: number; losses: number; draws: number; position: number | null } | null
  entries: EventQueueStandingRow[]
}
